import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";

export interface HelpRequest {
  id: string;
  user_id: string;
  course_id: string;
  module_id: string | null;
  topic_id: string | null;
  title: string;
  content: string;
  is_private: boolean;
  status: "pending" | "in_progress" | "resolved";
  resolved_by: string | null;
  resolved_at: string | null;
  created_at: string;
  updated_at: string;
  author?: {
    id: string;
    full_name: string;
    avatar_url: string | null;
  };
  resolver?: {
    id: string;
    full_name: string;
  } | null;
  topic?: {
    id: string;
    name: string;
  } | null;
}

export interface HelpRequestAllowance {
  remaining_this_week: number;
  cooldown_ends_at: string | null;
  can_request: boolean;
}

export function useHelpRequests(courseId: string, moduleId?: string | null) {
  return useQuery({
    queryKey: ["help-requests", courseId, moduleId],
    queryFn: async () => {
      let query = supabase
        .from("help_requests")
        .select("*")
        .eq("course_id", courseId)
        .order("created_at", { ascending: false });

      if (moduleId) {
        query = query.eq("module_id", moduleId);
      }

      const { data, error } = await query;
      if (error) throw error;

      // Fetch author profiles
      const userIds = [...new Set(data.map((r) => r.user_id))];
      const resolverIds = [...new Set(data.filter((r) => r.resolved_by).map((r) => r.resolved_by!))];
      const allIds = [...new Set([...userIds, ...resolverIds])];

      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, full_name, avatar_url")
        .in("id", allIds);

      const profileMap = new Map(profiles?.map((p) => [p.id, p]) || []);

      return data.map((request) => ({
        ...request,
        status: request.status as HelpRequest["status"],
        author: profileMap.get(request.user_id),
        resolver: request.resolved_by ? profileMap.get(request.resolved_by) : null,
      })) as HelpRequest[];
    },
    enabled: !!courseId,
  });
}

export function useHelpRequestAllowance() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["help-request-allowance", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;

      const { data, error } = await supabase.rpc("get_help_request_allowance", {
        _user_id: user.id,
      });

      if (error) throw error;
      return data as unknown as HelpRequestAllowance;
    },
    enabled: !!user?.id,
  });
}

export function useCreateHelpRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      course_id: string;
      module_id?: string | null;
      topic_id: string;
      title: string;
      content: string;
      is_private: boolean;
    }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("help_requests")
        .insert({
          user_id: user.id,
          course_id: params.course_id,
          module_id: params.module_id || null,
          topic_id: params.topic_id,
          title: params.title,
          content: params.content,
          is_private: params.is_private,
        })
        .select()
        .single();

      if (error) throw error;

      // Notify mentors, instructors, and admins
      try {
        await supabase.functions.invoke("notify-help-request", {
          body: { help_request_id: data.id },
        });
      } catch (notifyError) {
        console.error("Failed to send notifications:", notifyError);
        // Don't fail the mutation if notification fails
      }

      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["help-requests", variables.course_id] });
      queryClient.invalidateQueries({ queryKey: ["help-request-allowance"] });
      queryClient.invalidateQueries({ queryKey: ["all-help-requests"] });
    },
  });
}

export function useUpdateHelpRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      id: string;
      course_id: string;
      status?: HelpRequest["status"];
      resolved_by?: string;
    }) => {
      const updates: Record<string, unknown> = {};
      
      if (params.status) {
        updates.status = params.status;
        if (params.status === "resolved") {
          updates.resolved_at = new Date().toISOString();
          updates.resolved_by = params.resolved_by;
        }
      }

      const { data, error } = await supabase
        .from("help_requests")
        .update(updates)
        .eq("id", params.id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["help-requests", variables.course_id] });
    },
  });
}

export function useDeleteHelpRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { id: string; course_id: string }) => {
      const { error } = await supabase
        .from("help_requests")
        .delete()
        .eq("id", params.id);

      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["help-requests", variables.course_id] });
      queryClient.invalidateQueries({ queryKey: ["help-request-allowance"] });
    },
  });
}
